import type { NormalizedFieldValuesKey } from '@common/entities/fields-to-columns.types';
import { FieldType, InputFormatType } from '@common/enums';

export enum DarwinEnabledNormalizedFields {
  PURCHASE_PRICE = 'purchase_price',
  PENDING_DATE = 'pending_date',
  CLOSE_DATE = 'close_date',
  BUYER_BROKER_COMPENSATION_FROM_SELLER = 'buyer_broker_compensation_from_seller',
  BUYER_BROKER_COMPENSATION_FROM_SELLER_PERCENT = 'buyer_broker_compensation_from_seller_percent',
  LISTING_BROKER_COMPENSATION = 'listing_broker_compensation',
  LISTING_BROKER_COMPENSATION_PERCENT = 'listing_broker_compensation_percent',
}

export type DarwinFieldMapping = {
  label: string;
  darwinKey: string;
  fieldType: FieldType;
  inputFormat?: { type: InputFormatType };
};

const normalizedFieldToDarwinFields = {
  [DarwinEnabledNormalizedFields.PURCHASE_PRICE]: [
    {
      label: 'Purchase price',
      darwinKey: 'sellingPrice',
      fieldType: FieldType.NUMBER,
      inputFormat: { type: InputFormatType.CURRENCY },
    },
    {
      label: 'Commission price',
      darwinKey: 'commissionPrice',
      fieldType: FieldType.NUMBER,
      inputFormat: { type: InputFormatType.CURRENCY },
    },
  ],
  [DarwinEnabledNormalizedFields.PENDING_DATE]: [
    {
      label: 'Pending date',
      darwinKey: 'pendingDate',
      fieldType: FieldType.DATE,
    },
  ],
  [DarwinEnabledNormalizedFields.CLOSE_DATE]: [
    {
      label: 'Est close date',
      darwinKey: 'estimatedClosingDate',
      fieldType: FieldType.DATE,
    },
  ],
  // TODO: BLO-3190 Darwin sync: make sure buyer broker commission synced to Darwin includes commission paid by people other than the seller (listing agent, etc)
  [DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER_PERCENT]:
    [
      {
        label: 'Selling Commission Rate',
        darwinKey: 'sellCommRate',
        fieldType: FieldType.NUMBER,
        inputFormat: { type: InputFormatType.PERCENTAGE },
      },
    ],
  [DarwinEnabledNormalizedFields.BUYER_BROKER_COMPENSATION_FROM_SELLER]: [
    {
      label: 'Selling Commission Amount',
      darwinKey: 'sellCommission',
      fieldType: FieldType.NUMBER,
      inputFormat: { type: InputFormatType.CURRENCY },
    },
  ],
  [DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION_PERCENT]: [
    {
      label: 'Listing Commission Rate',
      darwinKey: 'listCommRate',
      fieldType: FieldType.NUMBER,
      inputFormat: { type: InputFormatType.PERCENTAGE },
    },
  ],
  [DarwinEnabledNormalizedFields.LISTING_BROKER_COMPENSATION]: [
    {
      label: 'Listing Commission Amount',
      darwinKey: 'listCommission',
      fieldType: FieldType.NUMBER,
      inputFormat: { type: InputFormatType.CURRENCY },
    },
  ],
} satisfies Record<DarwinEnabledNormalizedFields, DarwinFieldMapping[]> &
  Partial<Record<NormalizedFieldValuesKey, DarwinFieldMapping[]>>;

// NOTE: Avoid using this directly. Use KeyTermDarwinService.getDarwinFieldMappings instead.
export const NORMALIZED_FIELD_TO_DARWIN_FIELDS: Record<
  DarwinEnabledNormalizedFields,
  DarwinFieldMapping[]
> = normalizedFieldToDarwinFields;

export const parseDateValue = (value?: string | null): string | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const toDarwinDateFormat = (date: Date): string | null => {
    const timestamp = date.getTime();
    if (Number.isNaN(timestamp)) {
      return null;
    }

    // Darwin date format is YYYY-MM-DDTHH:MM:SS
    return date
      .toISOString()
      .replace('Z', '') // drop Z
      .replace(/\.\d{3}$/, ''); // drop .000
  };

  if (!value) {
    return null;
  }

  return toDarwinDateFormat(new Date(value));
};

export const parseCurrency = (
  value?: number | string | null
): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const sanitized = value.replace(/[$,]/g, '');
  const parsed = Number(sanitized);
  return Number.isFinite(parsed) ? parsed : null;
};
